/**
 * Admin Module Types
 *
 * Types for superadmin tenant management, feature flags, and system configuration.
 */

import type { TenantPlan, TenantStatus } from '@/features/tenant/types';

// =============================================================================
// TENANT MANAGEMENT TYPES
// =============================================================================

export interface AdminTenant {
  id: string;
  code: string;
  name: string;
  slug: string; // Schema/subdomain identifier
  email: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  timezone: string;
  locale: string;
  currency: string;
  status: TenantStatus;
  plan: TenantPlan;
  subscriptionStart: Date;
  subscriptionEnd?: Date;
  maxEmployees: number;
  maxStorage: number;
  employeeCount: number;
  storageUsed: number;
  adminCount: number;
  createdAt: Date;
  updatedAt: Date;
  lastActiveAt?: Date;
}

export interface TenantListFilters {
  search?: string;
  status?: TenantStatus | 'all';
  plan?: TenantPlan | 'all';
  country?: string;
  sortBy?: 'name' | 'createdAt' | 'employeeCount' | 'storageUsed' | 'lastActiveAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface TenantListResponse {
  data: AdminTenant[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TenantStats {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  suspendedTenants: number;
  totalEmployees: number;
  totalStorageBytes: number;
  newTenantsThisMonth: number;
  churnedTenantsThisMonth: number;
  revenueThisMonth: number;
  planDistribution: Record<TenantPlan, number>;
  countryDistribution: Array<{ country: string; count: number }>;
}

export interface CreateTenantData {
  name: string;
  slug: string;
  adminEmail: string;
  adminName: string;
  plan?: TenantPlan;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  maxEmployees?: number;
  maxStorage?: number;
  skipEmailVerification?: boolean;
  sendWelcomeEmail?: boolean;
}

export interface UpdateTenantData {
  name?: string;
  slug?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  plan?: TenantPlan;
  maxEmployees?: number;
  maxStorage?: number;
}

export interface TenantAction {
  type: 'suspend' | 'activate' | 'cancel' | 'extend_trial' | 'upgrade' | 'downgrade';
  reason?: string;
  newPlan?: TenantPlan;
  trialDays?: number;
}

export interface TenantLifecycleHistoryRecord {
  id: string;
  tenantId: string;
  previousStatus: TenantStatus;
  newStatus: TenantStatus;
  reason: string | null;
  notes: string | null;
  changedBy: string;
  changedByType: string;
  changedByDisplayName: string;
  changedByName: string | null;
  changedByEmail: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string | Date;
}

export interface TenantLifecycleHistoryResponse {
  data: TenantLifecycleHistoryRecord[];
  total: number;
}

export interface TenantLifecycleSummary {
  tenantId: string;
  tenantName: string;
  currentStatus: TenantStatus;
  statusSince: string | Date;
  daysSinceLastChange: number;
  totalStatusChanges: number;
  suspensionHistory: {
    totalSuspensions: number;
    lastSuspension: string | Date | null;
    averageSuspensionDays: number;
  };
}

export interface SuspendedTenantRecord {
  tenant: Pick<AdminTenant, 'id' | 'code' | 'name' | 'email' | 'status' | 'plan' | 'updatedAt'>;
  suspensionInfo: {
    suspendedAt: string | Date;
    reason: string | null;
    suspendedBy: string;
    metadata: Record<string, unknown> | null;
  } | null;
}

export interface SuspendedTenantListResponse {
  data: SuspendedTenantRecord[];
  total: number;
}

export type TenantStatusDistribution = Partial<Record<TenantStatus, number>>;

// =============================================================================
// PLATFORM USER TYPES
// =============================================================================

export interface GlobalAdminUser {
  id: string;
  email: string;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  tenantIds: string[];
  isActive: boolean;
  phone?: string | null;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GlobalAdminUserListResponse {
  data: GlobalAdminUser[];
  total: number;
  limit: number;
  offset: number;
}

// =============================================================================
// PLATFORM PLAN TYPES
// =============================================================================

export interface PlanFeatures {
  basicAttendance?: boolean;
  advancedAttendance?: boolean;
  basicLeave?: boolean;
  advancedLeave?: boolean;
  basicPayroll?: boolean;
  advancedPayroll?: boolean;
  reports?: boolean;
  advancedReports?: boolean;
  sso?: boolean;
  api?: boolean;
  webhooks?: boolean;
  auditLogs?: boolean;
  customFields?: boolean;
  multiLocation?: boolean;
  bulkOperations?: boolean;
  dataExport?: boolean;
  whiteLabeling?: boolean;
  prioritySupport?: boolean;
  dedicatedManager?: boolean;
}

export type PlanFeatureKey = keyof PlanFeatures;

export interface PlatformPlan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  monthlyPriceCents: number;
  annualPriceCents: number;
  maxEmployees: number;
  maxStorageBytes: number;
  maxAdmins: number;
  maxDepartments: number;
  apiRateLimitPerHour: number;
  auditLogRetentionDays: number;
  maxFileUploadBytes: number;
  features: PlanFeatures;
  isActive: boolean;
  isDefault: boolean;
  isPublic: boolean;
  isPopular: boolean;
  sortOrder: number;
  requiresApproval: boolean;
  requiresSalesContact: boolean;
  trialDays: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export interface PlatformPlanSummary {
  id: string;
  code: string;
  name: string;
  description: string | null;
  monthlyPriceUsd: number;
  annualPriceUsd: number;
  maxEmployees: number;
  maxStorageFormatted: string;
  isPopular: boolean;
  trialDays: number;
  features: PlanFeatures;
}

export interface PlatformPlanStats {
  total: number;
  active: number;
  public: number;
  tenantsByPlan: Record<string, number>;
}

export interface CreatePlatformPlanData {
  code: string;
  name: string;
  description?: string;
  monthlyPriceCents?: number;
  annualPriceCents?: number;
  maxEmployees?: number;
  maxStorageBytes?: number;
  maxAdmins?: number;
  maxDepartments?: number;
  apiRateLimitPerHour?: number;
  auditLogRetentionDays?: number;
  maxFileUploadBytes?: number;
  features?: PlanFeatures;
  isActive?: boolean;
  isDefault?: boolean;
  isPublic?: boolean;
  isPopular?: boolean;
  sortOrder?: number;
  requiresApproval?: boolean;
  requiresSalesContact?: boolean;
  trialDays?: number;
}

export type UpdatePlatformPlanData = Partial<Omit<CreatePlatformPlanData, 'code'>>;

export interface SeedPlatformPlansResult {
  created: number;
  existing: number;
}

// =============================================================================
// TENANT INVITE TYPES
// =============================================================================

export type TenantInviteRole = 'USER' | 'ADMIN' | 'SUPERADMIN';
export type TenantInviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface TenantInviteRecord {
  id: string;
  email: string;
  tenantId: string;
  role: TenantInviteRole | string;
  firstName: string | null;
  lastName: string | null;
  invitedBy: string;
  invitedByName: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  status: TenantInviteStatus;
}

export interface TenantInviteListResponse {
  data: TenantInviteRecord[];
  total: number;
}

export interface TenantInviteStats {
  pending: number;
  accepted: number;
  expired: number;
  revoked: number;
}

export interface InviteTenantUserData {
  email: string;
  firstName?: string;
  lastName?: string;
  role?: TenantInviteRole;
}

export interface TenantInviteResult {
  success: boolean;
  inviteId?: string;
  email: string;
  error?: string;
}

export interface BulkTenantInviteResult {
  total: number;
  successful: number;
  failed: number;
  results: TenantInviteResult[];
}

// =============================================================================
// FEATURE FLAG TYPES
// =============================================================================

export type FeatureFlagScope = 'global' | 'plan' | 'tenant';
export type FeatureFlagValueType = 'boolean' | 'string' | 'number' | 'json';

export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description?: string;
  scope: FeatureFlagScope;
  tenantId?: string | null;
  tenantName?: string;
  tenantCode?: string;
  valueType: FeatureFlagValueType;
  defaultValue: unknown;
  rolloutPercentage?: number;
  isEnabled: boolean;
  createdAt: Date;
  updatedAt: Date;
  expiresAt?: Date;
}

export interface FeatureFlagOverride {
  id: string;
  flagId: string;
  targetType: 'tenant' | 'plan';
  targetId: string;
  targetName: string;
  value: unknown;
  isEnabled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface FeatureFlagWithOverrides extends FeatureFlag {
  overrides: FeatureFlagOverride[];
  tenantCount?: number;
}

export interface CreateFeatureFlagData {
  key: string;
  name: string;
  description?: string;
  scope: FeatureFlagScope;
  tenantId?: string;
  valueType: FeatureFlagValueType;
  defaultValue: unknown;
  isEnabled: boolean;
}

export interface UpdateFeatureFlagData {
  name?: string;
  description?: string;
  defaultValue?: unknown;
  isEnabled?: boolean;
}

export interface CreateFeatureFlagOverrideData {
  targetType: 'tenant' | 'plan';
  targetId: string;
  value: unknown;
  isEnabled: boolean;
}

// =============================================================================
// SYSTEM CONFIG TYPES
// =============================================================================

export type SystemConfigDataType =
  | 'STRING'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'JSON'
  | 'ARRAY'
  | 'DATE'
  | 'EMAIL'
  | 'URL';

export interface SystemConfigTenantOverride {
  id: string;
  tenantId: string;
  tenantName?: string;
  tenantCode?: string;
  systemConfigId?: string;
  category?: string;
  key?: string;
  value: unknown;
  defaultValue?: unknown;
  dataType?: SystemConfigDataType;
  description?: string;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface SystemConfigTenantReference {
  id: string;
  tenantId: string;
  tenantName?: string;
  tenantCode?: string;
  value: unknown;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface SystemConfig {
  id: string;
  key: string;
  value: unknown;
  dataType: SystemConfigDataType;
  description?: string;
  category: string;
  isSecret: boolean;
  editableByTenant: boolean;
  validationRules?: unknown;
  defaultValue?: unknown;
  isActive: boolean;
  tenantOverrides: SystemConfigTenantOverride[];
  updatedAt: Date;
  updatedBy?: string;
}

export interface SystemConfigCategory {
  category: string;
  configs: SystemConfig[];
}

export interface CreateSystemConfigData {
  category: string;
  key: string;
  value: unknown;
  dataType: SystemConfigDataType;
  description?: string;
  editableByTenant?: boolean;
  validationRules?: unknown;
  defaultValue?: unknown;
}

export interface UpdateSystemConfigData {
  value?: unknown;
  description?: string;
  editableByTenant?: boolean;
  validationRules?: unknown;
  defaultValue?: unknown;
  isActive?: boolean;
}

export interface BulkSystemConfigUpsertResult {
  created: number;
  updated: number;
  errors: Array<{
    config: string;
    error: string;
  }>;
}

export interface ConfigurationOption {
  id: string;
  category: string;
  optionKey: string;
  optionValue: string;
  description?: string;
  metadata?: unknown;
  displayOrder: number;
  isActive: boolean;
  isSystem: boolean;
  tenantId?: string | null;
  tenantName?: string;
  tenantCode?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateConfigurationOptionData {
  category: string;
  optionKey: string;
  optionValue: string;
  description?: string;
  metadata?: unknown;
  displayOrder?: number;
  isSystem?: boolean;
  tenantId?: string;
}

export interface UpdateConfigurationOptionData {
  optionValue?: string;
  description?: string;
  metadata?: unknown;
  displayOrder?: number;
  isActive?: boolean;
}

export interface BulkConfigurationOptionResult {
  created: number;
  skipped: number;
  errors: Array<{
    optionKey: string;
    error: string;
  }>;
}

export type NotificationTemplateChannel = 'email' | 'sms' | 'push' | 'in_app';

export interface NotificationTemplate {
  id: string;
  key: string;
  name: string;
  type: NotificationTemplateChannel;
  category: string;
  subject?: string;
  template: string;
  variables?: Record<string, string>;
  isActive: boolean;
  tenantId?: string | null;
  tenantName?: string;
  tenantCode?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateNotificationTemplateData {
  key: string;
  name: string;
  type: NotificationTemplateChannel;
  category: string;
  subject?: string;
  template: string;
  variables?: string[];
  isActive?: boolean;
  tenantId?: string;
}

export interface UpdateNotificationTemplateData {
  name?: string;
  subject?: string;
  template?: string;
  variables?: string[];
  isActive?: boolean;
}

// =============================================================================
// AUDIT LOG TYPES
// =============================================================================

export interface AuditLog {
  id: string;
  action: string;
  status?: string;
  entityType: string;
  entityId: string;
  resourceType?: string;
  resourceId?: string;
  resource?: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  userRole?: string;
  tenantId?: string;
  tenantName?: string;
  targetUserId?: string;
  targetUserEmail?: string;
  targetUserName?: string;
  sessionId?: string;
  errorMessage?: string;
  country?: string;
  city?: string;
  oldValue?: unknown;
  newValue?: unknown;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  requiredPermission?: string;
  actualPermissions?: string[];
  reason?: string;
  createdAt: Date;
}

export interface AuditLogFilters {
  action?: string;
  status?: string;
  entityType?: string;
  entityId?: string;
  userId?: string;
  tenantId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface AuditLogListResponse {
  data: AuditLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface HrmsAuditLogEntry {
  id: string;
  entity: string;
  resourceType?: string;
  resourceId?: string;
  resource?: string;
  action: string;
  userId?: string;
  userEmail?: string;
  userRole?: string;
  timestamp: string;
  details?: Record<string, unknown>;
  method?: string;
  path?: string;
  ip?: string;
  userAgent?: string;
  success: boolean;
  requiredPermission?: string;
  actualPermissions?: string[];
  reason?: string;
}

export interface HrmsAuditLogFilters {
  entity?: string;
  resourceType?: string;
  resourceId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

export interface HrmsAuditLogsResponse {
  logs: HrmsAuditLogEntry[];
}

export interface HrmsAuditLogsExportResponse extends HrmsAuditLogsResponse {
  count: number;
}

export type HrmsAuditRetentionPolicySource = 'tenant-auth-config' | 'environment' | 'sap-default';

export interface HrmsAuditRetentionLastPurge {
  timestamp: string;
  deletedCount: number;
  retentionDays: number;
  policySource: HrmsAuditRetentionPolicySource;
  cutoffDate: string;
}

export interface HrmsAuditRetentionStatusResponse {
  retentionDays: number;
  policySource: HrmsAuditRetentionPolicySource;
  nextRunAtUtc: string;
  lastPurge?: HrmsAuditRetentionLastPurge;
}

export interface AuthAuditStats {
  totalEvents: number;
  successCount: number;
  failureCount: number;
  warningCount: number;
  uniqueUsers: number;
  uniqueIps: number;
  topActions: Array<{ action: string; count: number }>;
}

export interface AuthSecurityAlerts {
  failedLogins: number;
  accountLockouts: number;
  suspiciousIps: string[];
  mfaFailures: number;
}

export type AuditLogAction =
  // CRUD Actions (from DatabaseAuditInterceptor)
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  // Auth Actions (from auth_audit_logs)
  | 'LOGIN'
  | 'LOGIN_SUCCESS'
  | 'LOGIN_FAILED'
  | 'LOGIN_MFA_REQUIRED'
  | 'LOGIN_MFA_SUCCESS'
  | 'LOGIN_MFA_FAILED'
  | 'LOGIN_MAGIC_LINK'
  | 'LOGIN_PASSKEY'
  | 'LOGIN_SOCIAL'
  | 'LOGIN_SMS_OTP'
  | 'LOGOUT'
  | 'LOGOUT_ALL_SESSIONS'
  | 'PASSWORD_CHANGED'
  | 'PASSWORD_RESET_REQUESTED'
  | 'PASSWORD_RESET_SUCCESS'
  | 'PASSWORD_RESET_COMPLETED'
  | 'PASSWORD_CHANGE_FAILED'
  | 'MFA_SETUP_INITIATED'
  | 'MFA_ENABLED'
  | 'MFA_DISABLED'
  | 'MFA_FORCE_DISABLED'
  | 'MFA_BACKUP_CODES_GENERATED'
  | 'MFA_BACKUP_CODES_REGENERATED'
  | 'MFA_BACKUP_CODE_USED'
  | 'MFA_VERIFICATION_FAILED'
  | 'PASSKEY_REGISTERED'
  | 'PASSKEY_REMOVED'
  | 'PASSKEY_LOGIN'
  | 'PASSKEY_AUTH_FAILED'
  | 'PASSKEY_CLONE_DETECTED'
  | 'PASSKEY_DELETED'
  | 'OAUTH_CONNECTED'
  | 'OAUTH_DISCONNECTED'
  | 'OAUTH_LOGIN'
  | 'OAUTH_LINKED'
  | 'OAUTH_UNLINKED'
  | 'OAUTH_REGISTRATION'
  | 'SSO_LOGIN_INITIATED'
  | 'SSO_LOGIN_SUCCESS'
  | 'SSO_LOGIN_FAILED'
  | 'SSO_CONFIG_UPDATED'
  | 'SSO_METADATA_REFRESHED'
  | 'SESSION_CREATED'
  | 'SESSION_EXPIRED'
  | 'SESSION_REVOKED'
  | 'USER_SESSIONS_REVOKED'
  | 'DEVICE_TRUSTED'
  | 'DEVICE_TRUST_REVOKED'
  | 'DEVICE_TRUST_EXPIRED'
  | 'DEVICE_TRUST_VERIFIED'
  | 'DEVICE_MFA_SKIPPED'
  | 'ACCOUNT_LOCKED'
  | 'ACCOUNT_UNLOCKED'
  | 'SIGN_IN_COOLDOWN_RESET'
  | 'IP_BLOCKED'
  | 'IP_UNBLOCKED'
  | 'GLOBAL_IP_BLOCKED'
  | 'GLOBAL_IP_UNBLOCKED'
  | 'SUSPICIOUS_ACTIVITY'
  | 'USER_CREATED'
  | 'USER_UPDATED'
  | 'USER_DISABLED'
  | 'USER_ENABLED'
  | 'AUTH_CONFIG_UPDATED'
  | 'TENANT_AUTH_CONFIG_UPDATED'
  | 'TENANT_AUTH_CONFIG_RESET'
  | 'AUDIT_LOG_CLEANUP'
  | 'MAGIC_LINK_GENERATED'
  | 'MAGIC_LINK_VERIFIED'
  | 'MAGIC_LINK_VERIFICATION_FAILED'
  | 'INVITATION_SENT'
  | 'DATA_EXPORT_REQUESTED'
  | 'DATA_DELETION_REQUESTED'
  | 'DATA_DELETION_COMPLETED'
  | 'DATA_DELETION_FAILED'
  | 'CONSENT_GRANTED'
  | 'CONSENT_REVOKED'
  | 'EMAIL_SENT'
  | 'SMS_SENT'
  | 'NOTIFICATION_SENT'
  | 'OTP_SENT'
  | 'OTP_VERIFIED'
  | 'OTP_FAILED'
  | 'RISK_ASSESSMENT'
  | 'IMPOSSIBLE_TRAVEL'
  | 'STEP_UP_AUTH'
  | 'STEP_UP_AUTH_FAILED'
  | 'IMPERSONATION_START'
  | 'IMPERSONATION_END'
  // Business Actions
  | 'PERMISSION_CHANGE'
  | 'SETTINGS_CHANGE'
  | 'EXPORT'
  | 'IMPORT'
  | 'BULK_ACTION'
  | 'APPROVAL'
  | 'REJECTION';

export type AuditLogEntityType =
  // Core Entities
  | 'USER'
  | 'TENANT'
  | 'SESSION'
  // HRMS Entities
  | 'EMPLOYEE'
  | 'DEPARTMENT'
  | 'DESIGNATION'
  | 'LEAVE'
  | 'LEAVE_REQUEST'
  | 'ATTENDANCE'
  | 'SHIFT'
  | 'HOLIDAY'
  | 'ASSET'
  | 'PAYROLL'
  | 'BENEFIT'
  // CRM Entities
  | 'LEAD'
  | 'DEAL'
  | 'CONTACT'
  | 'ACCOUNT'
  | 'CAMPAIGN'
  | 'TASK'
  | 'NOTE'
  | 'ACTIVITY'
  | 'PRODUCT'
  | 'QUOTE'
  | 'VENDOR'
  | 'CASE'
  // Admin Entities
  | 'FEATURE_FLAG'
  | 'SYSTEM_CONFIG'
  | 'ROLE'
  | 'PERMISSION'
  // Billing Entities
  | 'SUBSCRIPTION'
  | 'INVOICE'
  | 'PLAN';

// =============================================================================
// HELPER CONSTANTS
// =============================================================================

export const TENANT_STATUS_OPTIONS: Array<{ value: TenantStatus; label: string }> = [
  { value: 'PENDING_EMAIL_VERIFICATION', label: 'Pending Verification' },
  { value: 'PENDING_PHONE_VERIFICATION', label: 'Pending Phone Verification' },
  { value: 'PENDING_REVIEW', label: 'Pending Review' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'TRIAL', label: 'Trial' },
];

export const PLAN_OPTIONS: Array<{ value: TenantPlan; label: string }> = [
  { value: 'FREE', label: 'Free' },
  { value: 'STARTER', label: 'Starter' },
  { value: 'PROFESSIONAL', label: 'Professional' },
  { value: 'ENTERPRISE', label: 'Enterprise' },
];

export const FEATURE_FLAG_SCOPE_OPTIONS: Array<{ value: FeatureFlagScope; label: string }> = [
  { value: 'global', label: 'Global' },
  { value: 'plan', label: 'Per Plan' },
  { value: 'tenant', label: 'Per Tenant' },
];

export const FEATURE_FLAG_VALUE_TYPE_OPTIONS: Array<{
  value: FeatureFlagValueType;
  label: string;
}> = [
  { value: 'boolean', label: 'Boolean' },
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'json', label: 'JSON' },
];

export const AUDIT_LOG_ACTION_LABELS: Record<AuditLogAction, string> = {
  // CRUD Actions
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  // Auth Actions
  LOGIN: 'Login',
  LOGIN_SUCCESS: 'Login Success',
  LOGIN_FAILED: 'Login Failed',
  LOGIN_MFA_REQUIRED: 'MFA Required',
  LOGIN_MFA_SUCCESS: 'MFA Login Success',
  LOGIN_MFA_FAILED: 'MFA Login Failed',
  LOGIN_MAGIC_LINK: 'Magic Link Login',
  LOGIN_PASSKEY: 'Passkey Login',
  LOGIN_SOCIAL: 'Social Login',
  LOGIN_SMS_OTP: 'SMS OTP Login',
  LOGOUT: 'Logout',
  LOGOUT_ALL_SESSIONS: 'Logout All Sessions',
  SESSION_CREATED: 'Session Created',
  SESSION_EXPIRED: 'Session Expired',
  SESSION_REVOKED: 'Session Revoked',
  USER_SESSIONS_REVOKED: 'User Sessions Revoked',
  PASSWORD_CHANGED: 'Password Changed',
  PASSWORD_RESET_REQUESTED: 'Password Reset Requested',
  PASSWORD_RESET_SUCCESS: 'Password Reset Success',
  PASSWORD_RESET_COMPLETED: 'Password Reset Completed',
  PASSWORD_CHANGE_FAILED: 'Password Change Failed',
  MFA_SETUP_INITIATED: 'MFA Setup Initiated',
  MFA_ENABLED: 'MFA Enabled',
  MFA_DISABLED: 'MFA Disabled',
  MFA_FORCE_DISABLED: 'MFA Force Disabled',
  MFA_BACKUP_CODES_GENERATED: 'MFA Backup Codes Generated',
  MFA_BACKUP_CODES_REGENERATED: 'MFA Backup Codes Regenerated',
  MFA_BACKUP_CODE_USED: 'MFA Backup Code Used',
  MFA_VERIFICATION_FAILED: 'MFA Verification Failed',
  PASSKEY_REGISTERED: 'Passkey Registered',
  PASSKEY_REMOVED: 'Passkey Removed',
  PASSKEY_LOGIN: 'Passkey Login',
  PASSKEY_AUTH_FAILED: 'Passkey Auth Failed',
  PASSKEY_CLONE_DETECTED: 'Passkey Clone Detected',
  PASSKEY_DELETED: 'Passkey Deleted',
  OAUTH_CONNECTED: 'OAuth Connected',
  OAUTH_DISCONNECTED: 'OAuth Disconnected',
  OAUTH_LOGIN: 'OAuth Login',
  OAUTH_LINKED: 'OAuth Linked',
  OAUTH_UNLINKED: 'OAuth Unlinked',
  OAUTH_REGISTRATION: 'OAuth Registration',
  SSO_LOGIN_INITIATED: 'SSO Login Initiated',
  SSO_LOGIN_SUCCESS: 'SSO Login Success',
  SSO_LOGIN_FAILED: 'SSO Login Failed',
  SSO_CONFIG_UPDATED: 'SSO Config Updated',
  SSO_METADATA_REFRESHED: 'SSO Metadata Refreshed',
  DEVICE_TRUSTED: 'Device Trusted',
  DEVICE_TRUST_REVOKED: 'Device Trust Revoked',
  DEVICE_TRUST_EXPIRED: 'Device Trust Expired',
  DEVICE_TRUST_VERIFIED: 'Device Trust Verified',
  DEVICE_MFA_SKIPPED: 'Device MFA Skipped',
  ACCOUNT_LOCKED: 'Account Locked',
  ACCOUNT_UNLOCKED: 'Account Unlocked',
  SIGN_IN_COOLDOWN_RESET: 'Sign-In Cooldown Reset',
  IP_BLOCKED: 'IP Blocked',
  IP_UNBLOCKED: 'IP Unblocked',
  GLOBAL_IP_BLOCKED: 'Global IP Blocked',
  GLOBAL_IP_UNBLOCKED: 'Global IP Unblocked',
  SUSPICIOUS_ACTIVITY: 'Suspicious Activity',
  USER_CREATED: 'User Created',
  USER_UPDATED: 'User Updated',
  USER_DISABLED: 'User Disabled',
  USER_ENABLED: 'User Enabled',
  AUTH_CONFIG_UPDATED: 'Auth Config Updated',
  TENANT_AUTH_CONFIG_UPDATED: 'Tenant Auth Config Updated',
  TENANT_AUTH_CONFIG_RESET: 'Tenant Auth Config Reset',
  AUDIT_LOG_CLEANUP: 'Audit Log Cleanup',
  MAGIC_LINK_GENERATED: 'Magic Link Generated',
  MAGIC_LINK_VERIFIED: 'Magic Link Verified',
  MAGIC_LINK_VERIFICATION_FAILED: 'Magic Link Verification Failed',
  INVITATION_SENT: 'Invitation Sent',
  DATA_EXPORT_REQUESTED: 'Data Export Requested',
  DATA_DELETION_REQUESTED: 'Data Deletion Requested',
  DATA_DELETION_COMPLETED: 'Data Deletion Completed',
  DATA_DELETION_FAILED: 'Data Deletion Failed',
  CONSENT_GRANTED: 'Consent Granted',
  CONSENT_REVOKED: 'Consent Revoked',
  EMAIL_SENT: 'Email Sent',
  SMS_SENT: 'SMS Sent',
  NOTIFICATION_SENT: 'Notification Sent',
  OTP_SENT: 'OTP Sent',
  OTP_VERIFIED: 'OTP Verified',
  OTP_FAILED: 'OTP Failed',
  RISK_ASSESSMENT: 'Risk Assessment',
  IMPOSSIBLE_TRAVEL: 'Impossible Travel',
  STEP_UP_AUTH: 'Step-Up Auth',
  STEP_UP_AUTH_FAILED: 'Step-Up Auth Failed',
  IMPERSONATION_START: 'Impersonation Started',
  IMPERSONATION_END: 'Impersonation Ended',
  // Business Actions
  PERMISSION_CHANGE: 'Permission Changed',
  SETTINGS_CHANGE: 'Settings Changed',
  EXPORT: 'Exported',
  IMPORT: 'Imported',
  BULK_ACTION: 'Bulk Action',
  APPROVAL: 'Approved',
  REJECTION: 'Rejected',
};

export const AUDIT_LOG_ACTION_COLORS: Partial<Record<AuditLogAction, string>> = {
  // CRUD Actions
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  // Auth Actions - Success
  LOGIN: 'bg-emerald-100 text-emerald-800',
  LOGIN_SUCCESS: 'bg-emerald-100 text-emerald-800',
  SESSION_CREATED: 'bg-emerald-100 text-emerald-800',
  // Auth Actions - Failure/Warning
  LOGIN_FAILED: 'bg-red-100 text-red-800',
  ACCOUNT_LOCKED: 'bg-red-100 text-red-800',
  // Auth Actions - Neutral
  LOGOUT: 'bg-gray-100 text-gray-800',
  LOGOUT_ALL_SESSIONS: 'bg-gray-100 text-gray-800',
  SESSION_EXPIRED: 'bg-gray-100 text-gray-800',
  SESSION_REVOKED: 'bg-orange-100 text-orange-800',
  // Auth Actions - Security
  PASSWORD_CHANGED: 'bg-yellow-100 text-yellow-800',
  PASSWORD_RESET_REQUESTED: 'bg-yellow-100 text-yellow-800',
  PASSWORD_RESET_COMPLETED: 'bg-yellow-100 text-yellow-800',
  MFA_ENABLED: 'bg-green-100 text-green-800',
  MFA_DISABLED: 'bg-orange-100 text-orange-800',
  ACCOUNT_UNLOCKED: 'bg-green-100 text-green-800',
  SIGN_IN_COOLDOWN_RESET: 'bg-blue-100 text-blue-800',
  // User Management
  USER_CREATED: 'bg-green-100 text-green-800',
  USER_UPDATED: 'bg-blue-100 text-blue-800',
  USER_DISABLED: 'bg-red-100 text-red-800',
  USER_ENABLED: 'bg-green-100 text-green-800',
  // Business Actions
  PERMISSION_CHANGE: 'bg-purple-100 text-purple-800',
  SETTINGS_CHANGE: 'bg-orange-100 text-orange-800',
  EXPORT: 'bg-cyan-100 text-cyan-800',
  IMPORT: 'bg-cyan-100 text-cyan-800',
  BULK_ACTION: 'bg-indigo-100 text-indigo-800',
  APPROVAL: 'bg-green-100 text-green-800',
  REJECTION: 'bg-red-100 text-red-800',
};

export const AUDIT_LOG_ENTITY_LABELS: Record<AuditLogEntityType, string> = {
  // Core Entities
  USER: 'User',
  TENANT: 'Tenant',
  SESSION: 'Session',
  // HRMS Entities
  EMPLOYEE: 'Employee',
  DEPARTMENT: 'Department',
  DESIGNATION: 'Designation',
  LEAVE: 'Leave',
  LEAVE_REQUEST: 'Leave Request',
  ATTENDANCE: 'Attendance',
  SHIFT: 'Shift',
  HOLIDAY: 'Holiday',
  ASSET: 'Asset',
  PAYROLL: 'Payroll',
  BENEFIT: 'Benefit',
  // CRM Entities
  LEAD: 'Lead',
  DEAL: 'Deal',
  CONTACT: 'Contact',
  ACCOUNT: 'Account',
  CAMPAIGN: 'Campaign',
  TASK: 'Task',
  NOTE: 'Note',
  ACTIVITY: 'Activity',
  PRODUCT: 'Product',
  QUOTE: 'Quote',
  VENDOR: 'Vendor',
  CASE: 'Case',
  // Admin Entities
  FEATURE_FLAG: 'Feature Flag',
  SYSTEM_CONFIG: 'System Config',
  ROLE: 'Role',
  PERMISSION: 'Permission',
  // Billing Entities
  SUBSCRIPTION: 'Subscription',
  INVOICE: 'Invoice',
  PLAN: 'Plan',
};

// =============================================================================
// ABAC / ROLE MANAGEMENT TYPES - Re-export from dedicated file
// =============================================================================
export * from './abac.types';
